import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ViewStyle,
} from 'react-native';
import { NavigationProp } from '@react-navigation/native';
import { AdminHeaderWithMenu, AdminErrorBoundary } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useAdminScreen } from '@/hooks/admin/useAdminScreen';

interface BaseAdminScreenProps {
  /** The current screen name for navigation highlighting */
  screenName: keyof AdminStackParamList;
  /** Navigation prop from React Navigation */
  navigation?: NavigationProp<AdminStackParamList>;
  /** Custom content to render in the main area */
  children?: React.ReactNode;
  /** Override the default title */
  title?: string;
  /** Override the default subtitle */
  subtitle?: string;
  /** Additional styles for the content container */
  contentContainerStyle?: ViewStyle;
  /** Whether to show the default placeholder content */
  showPlaceholder?: boolean;
  /** Whether scrolling is enabled (default: false) */
  scrollEnabled?: boolean;
  /** Custom logo press handler */
  onLogoPress?: () => void;
  /** Custom sign out handler (optional - uses default if not provided) */
  onSignOut?: () => void;
}

/**
 * BaseAdminScreen - Reusable base component for all admin screens
 * 
 * This component provides:
 * - Consistent layout and styling
 * - Navigation handling
 * - Error boundary wrapping
 * - Sign out functionality
 * - Placeholder content
 * - Performance optimizations
 */
export const BaseAdminScreen: React.FC<BaseAdminScreenProps> = React.memo(({
  screenName,
  navigation,
  children,
  title,
  subtitle,
  contentContainerStyle,
  showPlaceholder = true,
  scrollEnabled = false,
  onLogoPress,
  onSignOut,
}) => {
  // Use custom hook for admin screen logic
  const {
    screenMetadata,
    handleNavigate,
    handleLogoPress: defaultHandleLogoPress,
    handleSignOut: defaultHandleSignOut,
  } = useAdminScreen(screenName, navigation);
  
  // Use provided title/subtitle or fall back to metadata
  const displayTitle = title || screenMetadata.title;
  const displaySubtitle = subtitle || screenMetadata.subtitle;
  const currentPath = screenMetadata.path;

  // Override handlers if provided
  const finalLogoPress = useCallback(() => {
    if (onLogoPress) {
      onLogoPress();
    } else {
      defaultHandleLogoPress();
    }
  }, [onLogoPress, defaultHandleLogoPress]);

  const finalSignOut = useCallback(async () => {
    if (onSignOut) {
      await onSignOut();
    } else {
      await defaultHandleSignOut();
    }
  }, [onSignOut, defaultHandleSignOut]);

  return (
    <AdminErrorBoundary>
      <View style={styles.container}>
        <AdminHeaderWithMenu 
          onLogoPress={finalLogoPress}
          onSignOut={finalSignOut}
          onNavigate={handleNavigate}
          currentPath={currentPath}
        />
      
        {scrollEnabled ? (
          <ScrollView 
            style={styles.content}
            contentContainerStyle={[styles.contentContainer, contentContainerStyle]}
            showsVerticalScrollIndicator={false}
            scrollEnabled={scrollEnabled}
          >
            {children || (showPlaceholder && (
              <View style={styles.placeholderContainer}>
                <Text style={styles.title}>{displayTitle}</Text>
                <Text style={styles.subtitle}>{displaySubtitle}</Text>
              </View>
            ))}
          </ScrollView>
        ) : (
          <View style={[styles.content, styles.contentContainer, contentContainerStyle]}>
            {children || (showPlaceholder && (
              <View style={styles.placeholderContainer}>
                <Text style={styles.title}>{displayTitle}</Text>
                <Text style={styles.subtitle}>{displaySubtitle}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </AdminErrorBoundary>
  );
});

BaseAdminScreen.displayName = 'BaseAdminScreen';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 40,
    paddingBottom: 40,
  },
  placeholderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    textAlign: 'center',
    marginBottom: 16,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    color: ADMIN_COLORS.NAVIGATION.DESCRIPTION,
    textAlign: 'center',
  },
});
